import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../utils/api';

export const About = () => {
  const [siteSettings, setSiteSettings] = useState({});
  useEffect(() => { api.get('/site-settings').then(r => setSiteSettings(r.data || {})).catch(() => {}); }, []);

  return (
  <div className="min-h-screen" data-testid="about-page">
    {/* Hero Section */}
    <section className="relative h-[60vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#1F3D2B] to-[#2D5A3F]">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(194, 164, 109, 0.1) 35px, rgba(194, 164, 109, 0.1) 70px)'
        }}></div>
      </div>
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <h1 className="font-heading text-5xl sm:text-6xl text-ivory mb-6 tracking-tight">
          Our <em className="italic">Story</em>
        </h1>
        <p className="text-xl text-gold italic font-light tracking-wide">
          Rooted in Tradition, Woven with Purpose
        </p>
      </div>
    </section>

    {/* Founders Story */}
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-ivory">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-20">
          <div className="space-y-6">
            <h2 className="font-heading text-4xl text-forest mb-6 tracking-tight">
              Where It All <em className="italic">Began</em>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Vastram was born from the dreams of two remarkable women who dared to step beyond their comfort zones and create something beautiful together.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              One of them dedicated a decade to shaping young minds as a teacher. Her passion for nurturing and empowering others became the foundation of Vastram's ethos — a belief that every woman deserves to feel seen and celebrated.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The other spent years as a homemaker, mastering the art of creating warmth, comfort, and beauty in everyday life. Her eye for detail and her deep understanding of what makes a woman feel truly special became the heart of Vastram's curation.
            </p>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-sm overflow-hidden">
              <img
                src={siteSettings.our_story_image || "https://images.unsplash.com/photo-1739429945557-22682b674508?w=600"}
                alt="Founders"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-gold/20 backdrop-blur-sm p-8 rounded-sm">
              <p className="font-heading text-2xl text-forest italic">2 Women, 1 Dream</p>
            </div>
          </div>
        </div>

        {/* Mission Section */}
        <div className="bg-neutral-warm p-12 rounded-sm">
          <h3 className="font-heading text-3xl text-forest mb-8 text-center">
            Our <em className="italic">Mission</em>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto">
                <span className="text-3xl">👩</span>
              </div>
              <h4 className="font-heading text-xl text-forest">Celebrate Women</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Every woman deserves to feel beautiful, confident, and empowered in what she wears.
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto">
                <span className="text-3xl">🪷</span>
              </div>
              <h4 className="font-heading text-xl text-forest">Honor Culture</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We celebrate Indian heritage and the timeless elegance of traditional craftsmanship.
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto">
                <span className="text-3xl">🤝</span>
              </div>
              <h4 className="font-heading text-xl text-forest">Build Trust</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Vastram is a safe space where women trust us for quality, experience, and genuine care.
              </p>
            </div>
          </div>
        </div>

        {/* Vision Statement */}
        <div className="mt-20 text-center max-w-3xl mx-auto">
          <h3 className="font-heading text-3xl text-forest mb-6">
            A Safe <em className="italic">Space</em> for Women
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            Vastram isn't just a brand—it's a sanctuary where women feel understood, valued, and celebrated. We imagine a space where every woman can find pieces that make her feel special, where quality meets affordability, and where tradition embraces modernity.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed">
            From our founders' combined experience of nurturing, caring, and understanding what women truly need, Vastram was created to be trusted by women, for women.
          </p>
        </div>

        {/* Journey Timeline */}
        <div className="mt-20">
          <h3 className="font-heading text-3xl text-forest mb-12 text-center">
            Our <em className="italic">Journey</em>
          </h3>
          <div className="space-y-8">
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-24 text-right">
                <span className="text-sm uppercase tracking-wider text-gold font-medium">The Dream</span>
              </div>
              <div className="flex-shrink-0 w-3 h-3 bg-gold rounded-full mt-2"></div>
              <div className="flex-1 pb-8 border-b border-gold/20">
                <p className="text-muted-foreground leading-relaxed">
                  Two women with different paths but shared vision came together to celebrate womanhood and Indian culture.
                </p>
              </div>
            </div>
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-24 text-right">
                <span className="text-sm uppercase tracking-wider text-gold font-medium">The Start</span>
              </div>
              <div className="flex-shrink-0 w-3 h-3 bg-gold rounded-full mt-2"></div>
              <div className="flex-1 pb-8 border-b border-gold/20">
                <p className="text-muted-foreground leading-relaxed">
                  Vastram was born, curating the finest sarees and suits from artisans across India.
                </p>
              </div>
            </div>
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-24 text-right">
                <span className="text-sm uppercase tracking-wider text-gold font-medium">Today</span>
              </div>
              <div className="flex-shrink-0 w-3 h-3 bg-gold rounded-full mt-2"></div>
              <div className="flex-1">
                <p className="text-muted-foreground leading-relaxed">
                  A trusted destination where women find more than just clothing—they find confidence, tradition, and purpose woven into every thread.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-forest text-ivory">
      <div className="max-w-4xl mx-auto text-center">
        <h3 className="font-heading text-4xl mb-6 tracking-tight">
          Be Part of Our <em className="italic">Story</em>
        </h3>
        <p className="text-lg mb-10 text-ivory/90 leading-relaxed">
          Every saree, every suit carries the dreams of artisans and the vision of two women who believed in celebrating you.
        </p>
        <a
          href="/products"
          className="inline-block bg-gold text-forest px-8 py-4 rounded-none hover:bg-ivory transition-colors text-sm uppercase tracking-[0.1em]"
        >
          Explore Our Collection
        </a>
      </div>
    </section>
  </div>
  );
};

export const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailRe = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.name.trim() || formData.name.trim().length < 2) { toast.error('Please enter a valid name'); return; }
    if (!emailRe.test(formData.email.trim())) { toast.error('Please enter a valid email'); return; }
    if (formData.phone && !/^[6-9]\d{9}$/.test(formData.phone.trim())) { toast.error('Please enter a valid 10-digit phone number'); return; }
    if (!formData.message.trim() || formData.message.trim().length < 10) { toast.error('Message must be at least 10 characters'); return; }
    setLoading(true);
    try {
      await api.post('/contact', formData);
      toast.success('Thank you! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="contact-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-heading text-4xl sm:text-5xl text-forest mb-8 tracking-tight text-center">
          Get in <em className="italic">Touch</em>
        </h1>
        <p className="text-center text-muted-foreground mb-12">We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="bg-neutral-warm rounded-sm p-8 space-y-5">
            <div className="flex items-center gap-4">
              <span className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-forest" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-gold">Email</p>
                <a href="mailto:info@thevastramstore.com" className="text-sm text-forest hover:text-gold transition-colors" data-testid="contact-info-email">info@thevastramstore.com</a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-forest" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-gold">Phone / WhatsApp</p>
                <a href="tel:+919667881375" className="text-sm text-forest hover:text-gold transition-colors" data-testid="contact-info-phone">+91 9667881375</a>
              </div>
            </div>
            <div className="border-t border-gold/20 pt-5">
              <p className="text-xs uppercase tracking-[0.15em] text-gold mb-3">Follow Us</p>
              <div className="flex flex-wrap gap-3">
                <a href="https://www.instagram.com/thevastramstore.shop/" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gold/30 hover:border-forest text-sm text-forest hover:text-gold transition-colors" data-testid="contact-info-instagram">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  @thevastramstore.shop
                </a>
                <a href="https://www.facebook.com/profile.php?id=61590643284442" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gold/30 hover:border-forest text-sm text-forest hover:text-gold transition-colors" data-testid="contact-info-facebook">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  The Vastram Store
                </a>
                <a href="https://www.youtube.com/@thevastramstore" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gold/30 hover:border-forest text-sm text-forest hover:text-gold transition-colors" data-testid="contact-info-youtube">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  @thevastramstore
                </a>
                <a href="https://x.com/thevastramstore" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gold/30 hover:border-forest text-sm text-forest hover:text-gold transition-colors" data-testid="contact-info-x">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  @thevastramstore
                </a>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Your Name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              className="px-4 py-3 bg-neutral-warm border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
              data-testid="contact-name-input"
            />
            <input
              type="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              className="px-4 py-3 bg-neutral-warm border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
              data-testid="contact-email-input"
            />
          </div>
          <input
            type="tel"
            placeholder="Phone (Optional)"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="w-full px-4 py-3 bg-neutral-warm border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
            data-testid="contact-phone-input"
          />
          <textarea
            placeholder="Your Message"
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
            required
            rows={6}
            className="w-full px-4 py-3 bg-neutral-warm border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
            data-testid="contact-message-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-forest text-ivory px-8 py-4 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em] disabled:opacity-50"
            data-testid="contact-submit-button"
          >
            {loading ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
};

export const FAQ = () => (
  <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="faq-page">
    <div className="max-w-4xl mx-auto">
      <h1 className="font-heading text-4xl sm:text-5xl text-forest mb-12 tracking-tight text-center">
        Frequently Asked <em className="italic">Questions</em>
      </h1>
      <div className="space-y-8">
        <div className="bg-neutral-warm p-6 rounded-sm">
          <h3 className="font-heading text-xl text-forest mb-2">How do I choose the right size?</h3>
          <p className="text-muted-foreground leading-relaxed">All our sarees and suits come in standard sizing. Sarees are one-size-fits-all, while suits are available in S, M, L, XL, and XXL. Please refer to our size guide for detailed measurements.</p>
        </div>
        <div className="bg-neutral-warm p-6 rounded-sm">
          <h3 className="font-heading text-xl text-forest mb-2">What is your return policy?</h3>
          <p className="text-muted-foreground leading-relaxed">We offer a 7-day return policy for unworn and unwashed items with original tags intact. Please visit our Return Policy page for complete details.</p>
        </div>
        <div className="bg-neutral-warm p-6 rounded-sm">
          <h3 className="font-heading text-xl text-forest mb-2">How long does shipping take?</h3>
          <p className="text-muted-foreground leading-relaxed">We typically ship within 2-3 business days. Delivery usually takes 5-7 business days depending on your location within India.</p>
        </div>
        <div className="bg-neutral-warm p-6 rounded-sm">
          <h3 className="font-heading text-xl text-forest mb-2">Are the colors accurate?</h3>
          <p className="text-muted-foreground leading-relaxed">We make every effort to display colors as accurately as possible. However, slight variations may occur due to screen settings and lighting conditions.</p>
        </div>
      </div>
    </div>
  </div>
);

export const ReturnPolicy = () => (
  <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="return-policy-page">
    <div className="max-w-4xl mx-auto">
      <h1 className="font-heading text-4xl sm:text-5xl text-forest mb-8 tracking-tight text-center">
        Return <em className="italic">Policy</em>
      </h1>
      <div className="prose prose-lg max-w-none space-y-4 text-muted-foreground leading-relaxed">
        <h3 className="font-heading text-2xl text-forest mt-6 mb-3">7-Day Return Window</h3>
        <p>We accept returns within 7 days of delivery for items that are unworn, unwashed, and in original condition with all tags intact.</p>
        
        <h3 className="font-heading text-2xl text-forest mt-6 mb-3">How to Return</h3>
        <p>Contact our support team at info@thevastramstore.com with your order number and reason for return. We'll provide you with return instructions and a prepaid shipping label.</p>
        
        <h3 className="font-heading text-2xl text-forest mt-6 mb-3">Refund Process</h3>
        <p>Once we receive and inspect the returned item, we'll process your refund within 5-7 business days to your original payment method.</p>
        
        <h3 className="font-heading text-2xl text-forest mt-6 mb-3">Non-Returnable Items</h3>
        <p>Items marked as final sale, customized pieces, and discounted items (over 30% off) are not eligible for return.</p>
      </div>
    </div>
  </div>
);

export const FindYourSaree = () => {
  const [step, setStep] = useState(0);
  const [selections, setSelections] = useState({});
  const [quizOptions, setQuizOptions] = useState(null);
  const [results, setResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/products/filter-values').then(r => setQuizOptions({
      occasions: r.data.occasions || [],
      colors: r.data.colors || [],
      fabrics: r.data.fabrics || [],
    })).catch(() => {
      setQuizOptions({
        occasions: ["Wedding", "Festive", "Casual", "Party", "Daily Wear"],
        colors: ["Red", "Blue", "Green", "Pink", "Gold", "Black", "White"],
        fabrics: ["Silk", "Cotton", "Georgette", "Chiffon", "Banarasi", "Linen"]
      });
    });
  }, []);

  const steps = [
    { key: 'occasion', title: "What's the Occasion?", subtitle: 'Select the occasion you\'re dressing for', options: quizOptions?.occasions || [] },
    { key: 'color', title: 'Preferred Colour Palette?', subtitle: 'Choose a colour family you love', options: quizOptions?.colors || [] },
    { key: 'fabric', title: 'Fabric Preference?', subtitle: 'Which fabric feels right?', options: quizOptions?.fabrics || [] },
  ];

  const handleSelect = async (value) => {
    const newSelections = { ...selections, [steps[step].key]: value };
    setSelections(newSelections);
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      setLoadingResults(true);
      try {
        const params = new URLSearchParams();
        if (newSelections.occasion) params.append('tag', newSelections.occasion);
        if (newSelections.color) params.append('color', newSelections.color);
        if (newSelections.fabric) params.append('tag', newSelections.fabric);
        const res = await api.get(`/products?${params.toString()}&limit=20`);
        setResults(res.data.products || res.data || []);
      } catch { setResults([]); }
      setLoadingResults(false);
    }
  };

  const resetQuiz = () => { setStep(0); setSelections({}); setResults(null); };

  if (!quizOptions) return <div className="min-h-screen flex items-center justify-center"><p className="text-forest/60">Loading quiz...</p></div>;

  if (results !== null) {
    return (
      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="quiz-results">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="font-heading text-4xl text-forest mb-4 tracking-tight">Your Perfect <em className="italic">Matches</em></h1>
            <p className="text-muted-foreground">Based on: {Object.values(selections).join(' / ')}</p>
          </div>
          {results.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
              {results.map(p => (
                <div key={p.product_id} onClick={() => navigate(`/product/${p.product_id}`)} className="cursor-pointer group" data-testid="quiz-result-card">
                  <div className="aspect-[3/4] overflow-hidden rounded-sm mb-3">
                    <img src={p.images?.[0] || '/placeholder.jpg'} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <h3 className="text-sm text-forest font-medium truncate">{p.name}</h3>
                  <p className="text-sm text-gold">{'\u20B9'}{p.price?.toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg mb-4">No exact matches found for your preferences.</p>
              <p className="text-sm text-muted-foreground mb-8">Try exploring our full collection!</p>
            </div>
          )}
          <div className="flex justify-center gap-4 mt-12">
            <button onClick={resetQuiz} className="border border-forest text-forest px-8 py-3 rounded-none text-sm uppercase tracking-[0.1em] hover:bg-forest hover:text-ivory transition-colors" data-testid="quiz-retake-button">Retake Quiz</button>
            <a href="/products" className="bg-forest text-ivory px-8 py-3 rounded-none text-sm uppercase tracking-[0.1em] hover:bg-gold hover:text-forest transition-colors inline-block" data-testid="quiz-explore-all">Explore All Products</a>
          </div>
        </div>
      </div>
    );
  }

  const currentStep = steps[step];
  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="find-your-saree">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl text-forest mb-6 tracking-tight">Find Your Perfect <em className="italic">Saree</em></h1>
          <p className="text-muted-foreground">Answer a few questions and we'll curate the best options for you.</p>
          <div className="flex justify-center gap-2 mt-6">
            {steps.map((_, i) => (<div key={i} className={`h-1.5 w-12 rounded-full transition-colors ${i <= step ? 'bg-gold' : 'bg-gold/20'}`} />))}
          </div>
        </div>
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl text-forest mb-2">{currentStep.title}</h2>
          <p className="text-sm text-muted-foreground">{currentStep.subtitle}</p>
        </div>
        {loadingResults ? (
          <div className="text-center py-16"><p className="text-forest/60">Finding your perfect sarees...</p></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {currentStep.options.map((option) => (
              <button key={option} onClick={() => handleSelect(option)}
                className={`p-5 rounded-sm border-2 text-center transition-all hover:border-forest hover:bg-forest/5 ${selections[currentStep.key] === option ? 'border-forest bg-forest/10' : 'border-gold/30'}`}
                data-testid={`quiz-option-${option.toLowerCase().replace(/\s+/g, '-')}`}>
                <p className="font-heading text-lg text-forest">{option}</p>
              </button>
            ))}
          </div>
        )}
        {step > 0 && (<div className="text-center mt-6"><button onClick={() => setStep(step - 1)} className="text-sm text-forest hover:text-gold underline transition-colors">Back to previous step</button></div>)}
      </div>
    </div>
  );
};

export const DrapeStories = () => {
  const navigate = useNavigate();

  const stories = [
    {
      id: 'banarasi-weaving',
      title: 'The Art of Banarasi Weaving',
      date: 'January 15, 2026',
      author: 'Vastram Team',
      category: 'Heritage',
      image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=800',
      excerpt: 'Discover the centuries-old tradition of Banarasi silk weaving and the artisans who keep it alive.',
    },
    {
      id: 'choosing-perfect-saree',
      title: 'Choosing the Perfect Saree',
      date: 'January 10, 2026',
      author: 'Priya Sharma',
      category: 'Style Guide',
      image: 'https://images.unsplash.com/photo-1739429945851-cf348b087e3d?w=800',
      excerpt: 'A comprehensive guide to selecting the right saree for every occasion and body type.',
    },
    {
      id: 'saree-draping-styles',
      title: 'Saree Draping Styles Across India',
      date: 'January 5, 2026',
      author: 'Meera Reddy',
      category: 'Tutorial',
      image: 'https://images.unsplash.com/photo-1739429944341-db4eefcd230a?w=800',
      excerpt: 'Explore different regional draping styles and find the one that suits you best.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="drape-stories-page">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#1F3D2B] to-[#2D5A3F]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(194, 164, 109, 0.1) 35px, rgba(194, 164, 109, 0.1) 70px)'
          }}></div>
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="font-heading text-5xl sm:text-6xl text-ivory mb-4 tracking-tight">
            Drape <em className="italic">Stories</em>
          </h1>
          <p className="text-lg italic font-light tracking-wide" style={{ color: '#E8E0D4' }}>
            Tradition, style, and the timeless art of the saree
          </p>
        </div>
      </section>

      {/* Featured Story */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-ivory">
        <div className="max-w-6xl mx-auto">
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-0 cursor-pointer group"
            onClick={() => navigate(`/drape-stories/${stories[0].id}`)}
          >
            <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
              <img
                src={stories[0].image}
                alt={stories[0].title}
                className="w-full h-full object-cover product-image-hover"
              />
            </div>
            <div className="bg-neutral-warm p-8 sm:p-12 flex flex-col justify-center">
              <span className="text-xs uppercase tracking-[0.2em] text-gold mb-3">{stories[0].category} &middot; {stories[0].date}</span>
              <h2 className="font-heading text-3xl text-forest mb-4 group-hover:text-gold transition-colors">
                {stories[0].title}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{stories[0].excerpt}</p>
              <span className="text-sm uppercase tracking-[0.2em] text-forest group-hover:text-gold transition-colors">
                Read Story &rarr;
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* More Stories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl text-forest mb-10 text-center">
            More <em className="italic">Stories</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {stories.slice(1).map((story) => (
              <div
                key={story.id}
                className="cursor-pointer group"
                onClick={() => navigate(`/drape-stories/${story.id}`)}
                data-testid="story-card"
              >
                <div className="aspect-[16/9] overflow-hidden rounded-sm mb-5">
                  <img
                    src={story.image}
                    alt={story.title}
                    className="w-full h-full object-cover product-image-hover"
                  />
                </div>
                <span className="text-xs uppercase tracking-[0.2em] text-gold">{story.category} &middot; {story.date}</span>
                <h3 className="font-heading text-2xl text-forest mt-2 mb-3 group-hover:text-gold transition-colors">
                  {story.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">{story.excerpt}</p>
                <p className="text-sm text-forest/60 mt-3">By {story.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Explore All Button */}
      <section className="py-12 px-4 text-center">
        <button
          onClick={() => navigate('/drape-stories/all')}
          className="inline-block bg-forest text-ivory px-10 py-4 text-sm uppercase tracking-[0.15em] hover:bg-gold hover:text-forest transition-colors"
          data-testid="explore-all-stories-btn"
        >
          Explore All Stories
        </button>
      </section>
    </div>
  );
};

const PolicyPage = ({ title, testId, children }) => (
  <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid={testId}>
    <div className="max-w-4xl mx-auto">
      <h1 className="font-heading text-4xl sm:text-5xl text-forest mb-8 tracking-tight text-center">{title}</h1>
      <div className="prose prose-lg max-w-none space-y-4 text-muted-foreground leading-relaxed">{children}</div>
      <div className="mt-12 text-center text-sm text-muted-foreground">
        <p>For any queries, reach us at <a href="mailto:info@thevastramstore.com" className="text-forest hover:text-gold">info@thevastramstore.com</a> or call <a href="tel:+919667881375" className="text-forest hover:text-gold">+91 9667881375</a></p>
        <p className="mt-1">Vastram, KH-12/17/3, Railway Road, Delhi-42 (Pin: 110042)</p>
      </div>
    </div>
  </div>
);

export const PrivacyPolicy = () => (<PolicyPage title={<>Privacy <em className="italic">Policy</em></>} testId="privacy-policy-page"><p className="text-sm text-muted-foreground mb-4">Last updated: April 2026</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">1. Information We Collect</h3><p>When you visit thevastramstore.com, we collect: your name, email, phone number, shipping address, and payment information when you place an order. We also collect browsing data such as IP address, browser type, and pages visited through cookies.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">2. How We Use Your Information</h3><p>We use your information to: process and deliver your orders, send order confirmations and shipping updates, respond to your inquiries, improve our website, send promotional offers (only with consent), and prevent fraud.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">3. Information Sharing</h3><p>We do not sell your personal information. We share data only with: payment processors (Razorpay), shipping partners, and email service providers. All third parties are bound by confidentiality agreements.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">4. Data Security</h3><p>We implement industry-standard security measures including SSL encryption, secure payment processing, and restricted access to personal data.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">5. Your Rights</h3><p>You may request access to, correction of, or deletion of your personal data by emailing info@thevastramstore.com. We will respond within 30 days.</p></PolicyPage>);

export const TermsConditions = () => (<PolicyPage title={<>Terms & <em className="italic">Conditions</em></>} testId="terms-conditions-page"><p className="text-sm text-muted-foreground mb-4">Last updated: April 2026</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">1. General</h3><p>By accessing thevastramstore.com, you agree to these Terms & Conditions. Vastram reserves the right to modify these terms at any time.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">2. Products & Pricing</h3><p>All products are subject to availability. Prices are in Indian Rupees (INR) and include applicable taxes as displayed at checkout. Colors may vary slightly due to display settings.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">3. Orders & Payment</h3><p>We accept online payments via Razorpay (UPI, cards, net banking) and Cash on Delivery. We reserve the right to cancel orders due to pricing errors, stock issues, or suspected fraud.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">4. Shipping</h3><p>We ship across India. Orders are dispatched within 2-3 business days. Delivery takes 5-7 business days.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">5. Intellectual Property</h3><p>All content on thevastramstore.com is the property of Vastram and protected by copyright laws.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">6. Governing Law</h3><p>These terms are governed by the laws of India. Disputes shall be subject to the exclusive jurisdiction of courts in Delhi.</p></PolicyPage>);

export const ShippingPolicy = () => (<PolicyPage title={<>Shipping <em className="italic">Policy</em></>} testId="shipping-policy-page"><h3 className="font-heading text-2xl text-forest mt-6 mb-3">Processing Time</h3><p>Orders are processed within 1-3 business days. You will receive a shipping confirmation email with tracking details once dispatched.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">Shipping Rates</h3><p>We offer <strong>free shipping</strong> on all orders across India. Standard delivery takes 5-7 business days. Remote areas may take 7-10 business days.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">Delivery Issues</h3><p>If your order is delayed, damaged, or lost in transit, contact us at info@thevastramstore.com or call +91 9667881375.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">Undeliverable Packages</h3><p>If a package is returned due to incorrect address or failed delivery, we will contact you to arrange re-shipping.</p></PolicyPage>);

export const CancellationPolicy = () => (<PolicyPage title={<>Cancellation <em className="italic">Policy</em></>} testId="cancellation-policy-page"><h3 className="font-heading text-2xl text-forest mt-6 mb-3">Order Cancellation</h3><p>You may cancel within <strong>60 minutes</strong> of placing your order if it has not been shipped. Go to My Orders and click "Cancel Order".</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">Refund on Cancellation</h3><p>Prepaid orders cancelled before shipping get a full refund within 5-7 business days. COD orders require no refund processing.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">Cancellation by Vastram</h3><p>We reserve the right to cancel orders for pricing errors, stock unavailability, suspected fraud, or address issues. Full refund will be processed.</p></PolicyPage>);

export const RefundPolicy = () => (<PolicyPage title={<>Refund <em className="italic">Policy</em></>} testId="refund-policy-page"><h3 className="font-heading text-2xl text-forest mt-6 mb-3">Eligibility</h3><p>Refunds apply to: items returned within 7 days (unworn, unwashed, with tags), defective/damaged products, and wrong items received.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">Refund Process</h3><p>After receiving and inspecting the returned item (2-3 business days), we notify you. Approved refunds are processed within 5-7 business days.</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">Refund Methods</h3><p><strong>Online:</strong> Refunded to original payment method. <strong>COD:</strong> Refunded via bank transfer (NEFT/IMPS).</p><h3 className="font-heading text-2xl text-forest mt-6 mb-3">Non-Refundable</h3><p>Final sale items, customized products, items without tags, and items showing signs of use are not eligible.</p></PolicyPage>);
